import { Button } from "@package/subframe/components/Button";
import { Table } from "@package/subframe/components/Table";
import { keepPreviousData, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { type ReactNode, useState } from "react";
import { toast } from "sonner";

import { WorkspaceStatusBadge } from "@/components/checklists/WorkspaceStatusBadge";
import { CountBadge } from "@/components/ui/count-badge";
import { Pagination } from "@/components/ui/pagination";
import { Spinner } from "@/components/ui/spinner";
import {
  TABLE_MIN_WIDTH,
  TableScrollContainer,
} from "@/components/ui/table-scroll-container";
import {
  useAcknowledgementTrackingQueryControllerStaffAcknowledgements,
  useManageAcknowledgementItemCommandControllerRemind,
} from "@/generated/acknowledgements/acknowledgements";
import type { AcknowledgementRecordDto } from "@/generated/model/acknowledgementRecordDto";
import type { MyAcknowledgementRequestDto } from "@/generated/model/myAcknowledgementRequestDto";
import type { StaffAcknowledgementsDto } from "@/generated/model/staffAcknowledgementsDto";
import { useIsMobile } from "@/hooks/use-mobile";
import { ACK_COPY } from "@/lib/acknowledgements/copy";
import { invalidateAcknowledgementQueries } from "@/lib/acknowledgements/invalidate-acknowledgement-queries";
import { REMINDER_ACTIONS_ENABLED } from "@/lib/acknowledgements/tracking";
import { extractApiErrorMessage } from "@/lib/compliance/invalidate-compliance-queries";
import {
  completedDates,
  hasNoAcknowledgements,
  isAcknowledgementsWithheld,
  itemSummary,
  outstandingDates,
  PROFILE_ACKNOWLEDGEMENTS_PAGE_SIZE,
} from "@/lib/people-acknowledgements";
import { cn } from "@/lib/utils";
import {
  ACKNOWLEDGEMENT_SUPERSEDED_PILL,
  acknowledgementRequestPill,
} from "@/lib/workspace/status-pill";

import { ProfileSectionCard } from "../ProfileSectionCard";

interface AcknowledgementsSectionProps {
  nurseryId: string;
  userId: string;
  isOwnProfile: boolean;
  /** Managers and above, never on their own profile; the buttons also wait on REMINDER_ACTIONS_ENABLED. */
  canSendReminders: boolean;
}

/**
 * The person's side of the acknowledgement records NAU-410 owns (AC22-AC25).
 * Outstanding requests are always the full list; only the signed history pages.
 */
export function AcknowledgementsSection({
  nurseryId,
  userId,
  isOwnProfile,
  canSendReminders,
}: AcknowledgementsSectionProps) {
  const [page, setPage] = useState(1);
  const query = useAcknowledgementTrackingQueryControllerStaffAcknowledgements(
    nurseryId,
    userId,
    {
      limit: PROFILE_ACKNOWLEDGEMENTS_PAGE_SIZE,
      offset: (page - 1) * PROFILE_ACKNOWLEDGEMENTS_PAGE_SIZE,
    },
    {
      query: {
        refetchOnMount: "always",
        gcTime: 0,
        placeholderData: keepPreviousData,
      },
    },
  );

  if (query.isError && isAcknowledgementsWithheld(query.error)) {
    return (
      <ProfileSectionCard
        title="Acknowledgements"
        testId="profile-section-ack-withheld"
      >
        <Muted>These aren&apos;t available to you.</Muted>
      </ProfileSectionCard>
    );
  }

  const view = query.data?.data;
  const notReady = notReadyState({
    view,
    isPaused: query.isPaused,
    isPlaceholderData: query.isPlaceholderData,
    isError: query.isError,
    page,
    onPageChange: setPage,
    onRetry: () => void query.refetch(),
  });

  return (
    <div
      data-testid="profile-section-acknowledgements"
      className="flex w-full flex-col gap-6"
    >
      <ProfileSectionCard
        title="Outstanding"
        testId="profile-section-ack-outstanding"
        action={view && <CountBadge count={view.outstanding.length} />}
      >
        {notReady ??
          (view && (
            <OutstandingBody
              view={view}
              isOwnProfile={isOwnProfile}
              canSendReminders={canSendReminders}
            />
          ))}
      </ProfileSectionCard>
      {!notReady && view && !hasNoAcknowledgements(view) && (
        <ProfileSectionCard
          title="History"
          testId="profile-section-ack-history"
        >
          <HistoryBody
            view={view}
            isPlaceholderData={query.isPlaceholderData}
            isFetching={query.isFetching}
            isPaused={query.isPaused}
            page={page}
            onPageChange={setPage}
          />
        </ProfileSectionCard>
      )}
    </div>
  );
}

/** What to show instead of the lists while they can't be shown, or null once they can. */
function notReadyState({
  view,
  isPaused,
  isPlaceholderData,
  isError,
  page,
  onPageChange,
  onRetry,
}: {
  view: StaffAcknowledgementsDto | undefined;
  isPaused: boolean;
  isPlaceholderData: boolean;
  isError: boolean;
  page: number;
  onPageChange: (page: number) => void;
  onRetry: () => void;
}): ReactNode {
  if (isPaused && !view) {
    return <Muted>Reconnect to see these.</Muted>;
  }
  // Offline after a page change, `view` is still the previous page.
  if (isPaused && isPlaceholderData) {
    return <Muted>Reconnect to see these.</Muted>;
  }
  if (isError) {
    return (
      <div className="flex flex-col items-start gap-2">
        <span className="text-body font-body text-error-700">
          Couldn&apos;t load these.
        </span>
        <div className="flex items-center gap-2">
          <Button variant="neutral-secondary" size="small" onClick={onRetry}>
            Try again
          </Button>
          {page > 1 && (
            <Button
              variant="neutral-tertiary"
              size="small"
              onClick={() => onPageChange(1)}
            >
              Back to page 1
            </Button>
          )}
        </div>
      </div>
    );
  }
  if (!view) {
    return (
      <div className="flex w-full items-center justify-center py-6">
        <Spinner />
      </div>
    );
  }
  if (hasNoAcknowledgements(view)) {
    return (
      <div data-testid="profile-ack-empty" className="flex flex-col gap-1">
        <Muted>Nothing has been sent to read and sign.</Muted>
        <Muted>
          At the moment only managers and deputy managers are asked to read and
          sign documents.
        </Muted>
      </div>
    );
  }
  return null;
}

function OutstandingBody({
  view,
  isOwnProfile,
  canSendReminders,
}: {
  view: StaffAcknowledgementsDto;
  isOwnProfile: boolean;
  canSendReminders: boolean;
}) {
  const isMobile = useIsMobile();
  const showReminders = REMINDER_ACTIONS_ENABLED && canSendReminders;

  if (view.outstanding.length === 0) {
    return <Muted>Nothing outstanding.</Muted>;
  }
  if (isMobile) {
    return (
      <ul className="flex w-full flex-col gap-4">
        {view.outstanding.map((request) => (
          <OutstandingRow
            key={request.requestId}
            request={request}
            isOwnProfile={isOwnProfile}
            showReminders={showReminders}
          />
        ))}
      </ul>
    );
  }

  const hasActions = isOwnProfile || showReminders;
  return (
    <TableScrollContainer>
      <Table
        className={TABLE_MIN_WIDTH.md}
        header={
          <Table.HeaderRow>
            <Table.HeaderCell>Document</Table.HeaderCell>
            <Table.HeaderCell>Sent</Table.HeaderCell>
            <Table.HeaderCell>Due</Table.HeaderCell>
            <Table.HeaderCell>Read</Table.HeaderCell>
            <Table.HeaderCell>Status</Table.HeaderCell>
            {hasActions && (
              <Table.HeaderCell variant="aligned-right">
                Actions
              </Table.HeaderCell>
            )}
          </Table.HeaderRow>
        }
      >
        {view.outstanding.map((request) => {
          const dates = outstandingDates(request);
          return (
            <Table.Row
              key={request.requestId}
              data-testid={`profile-ack-outstanding-${request.requestId}`}
            >
              <Table.Cell className="h-14 grow-[2] shrink-0 basis-0">
                <ItemHeading
                  title={request.title}
                  summary={itemSummary(request.itemType, request.version)}
                />
              </Table.Cell>
              <DateCell value={dates.sent} />
              <DateCell value={dates.due} />
              <DateCell value={dates.read} />
              <Table.Cell className="h-14 grow shrink-0 basis-0">
                <WorkspaceStatusBadge
                  pill={acknowledgementRequestPill(request)}
                />
              </Table.Cell>
              {hasActions && (
                <Table.Cell
                  variant="aligned-right"
                  className="h-14 grow shrink-0 basis-0"
                >
                  <OutstandingAction
                    request={request}
                    isOwnProfile={isOwnProfile}
                    showReminders={showReminders}
                  />
                </Table.Cell>
              )}
            </Table.Row>
          );
        })}
      </Table>
    </TableScrollContainer>
  );
}

function HistoryBody({
  view,
  isPlaceholderData,
  isFetching,
  isPaused,
  page,
  onPageChange,
}: {
  view: StaffAcknowledgementsDto;
  isPlaceholderData: boolean;
  isFetching: boolean;
  isPaused: boolean;
  page: number;
  onPageChange: (page: number) => void;
}) {
  const isMobile = useIsMobile();

  return (
    <div className="flex w-full flex-col gap-3">
      {view.completedTotal === 0 ? (
        <Muted>Nothing signed yet.</Muted>
      ) : (
        <div
          data-testid="profile-ack-signed-list"
          aria-busy={isPlaceholderData}
          className={cn(
            "flex w-full flex-col transition-opacity",
            isPlaceholderData && "opacity-50",
          )}
        >
          {isMobile ? (
            <ul className="flex w-full flex-col gap-4">
              {view.completed.map((record) => (
                <SignedRow key={record.recordId} record={record} />
              ))}
            </ul>
          ) : (
            <HistoryTable records={view.completed} />
          )}
        </div>
      )}
      {view.completedTotal > PROFILE_ACKNOWLEDGEMENTS_PAGE_SIZE && (
        <Pagination
          page={page}
          pageSize={PROFILE_ACKNOWLEDGEMENTS_PAGE_SIZE}
          totalCount={view.completedTotal}
          itemNoun="signed documents"
          onPageChange={onPageChange}
          showGoToPage={false}
          disabled={isFetching || isPaused}
        />
      )}
      <span className="text-caption font-caption text-subtext-color">
        Signatures cannot be edited or removed.
      </span>
    </div>
  );
}

function HistoryTable({ records }: { records: AcknowledgementRecordDto[] }) {
  return (
    <TableScrollContainer>
      <Table
        header={
          <Table.HeaderRow>
            <Table.HeaderCell>Document</Table.HeaderCell>
            <Table.HeaderCell>Read</Table.HeaderCell>
            <Table.HeaderCell>Signed</Table.HeaderCell>
            <Table.HeaderCell>Status</Table.HeaderCell>
          </Table.HeaderRow>
        }
      >
        {records.map((record) => {
          const dates = completedDates(record);
          return (
            <Table.Row
              key={record.recordId}
              data-testid={`profile-ack-completed-${record.recordId}`}
            >
              <Table.Cell className="h-14 grow-[2] shrink-0 basis-0">
                <ItemHeading
                  title={record.itemTitle}
                  summary={itemSummary(
                    record.itemType,
                    record.itemVersionNumber,
                  )}
                />
              </Table.Cell>
              <DateCell value={dates.read} />
              <DateCell value={dates.signed} />
              <Table.Cell className="h-14 grow shrink-0 basis-0">
                {record.supersededVersion && (
                  <div
                    data-testid={`profile-ack-superseded-${record.recordId}`}
                  >
                    <WorkspaceStatusBadge
                      pill={ACKNOWLEDGEMENT_SUPERSEDED_PILL}
                    />
                  </div>
                )}
              </Table.Cell>
            </Table.Row>
          );
        })}
      </Table>
    </TableScrollContainer>
  );
}

function DateCell({ value }: { value: string }) {
  return (
    <Table.Cell className="h-14 grow shrink-0 basis-0">
      <span className="text-body font-body text-subtext-color">{value}</span>
    </Table.Cell>
  );
}

function OutstandingAction({
  request,
  isOwnProfile,
  showReminders,
}: {
  request: MyAcknowledgementRequestDto;
  isOwnProfile: boolean;
  showReminders: boolean;
}) {
  const navigate = useNavigate();

  if (isOwnProfile) {
    return (
      <Button
        variant="neutral-secondary"
        size="small"
        aria-label={`Open ${request.title}, version ${request.version}`}
        data-testid={`profile-ack-open-${request.requestId}`}
        onClick={() =>
          void navigate({
            to: "/acknowledgements/$requestId",
            params: { requestId: request.requestId },
          })
        }
      >
        Open
      </Button>
    );
  }
  if (!showReminders) {
    return null;
  }
  return <SendReminderButton request={request} />;
}

function SendReminderButton({
  request,
}: {
  request: MyAcknowledgementRequestDto;
}) {
  const queryClient = useQueryClient();
  const remind = useManageAcknowledgementItemCommandControllerRemind();

  const handleRemind = async () => {
    try {
      await remind.mutateAsync({ requestId: request.requestId });
      toast.success(ACK_COPY.TOAST_REMINDED);
      await invalidateAcknowledgementQueries(queryClient);
    } catch (error) {
      toast.error(extractApiErrorMessage(error, ACK_COPY.TOAST_REMIND_ERROR));
    }
  };

  return (
    <Button
      variant="neutral-secondary"
      size="small"
      aria-label={`Send a reminder about ${request.title}`}
      data-testid={`profile-ack-remind-${request.requestId}`}
      loading={remind.isPending}
      disabled={remind.isPending}
      onClick={() => void handleRemind()}
    >
      Send reminder
    </Button>
  );
}

function OutstandingRow({
  request,
  isOwnProfile,
  showReminders,
}: {
  request: MyAcknowledgementRequestDto;
  isOwnProfile: boolean;
  showReminders: boolean;
}) {
  const dates = outstandingDates(request);

  return (
    <li
      data-testid={`profile-ack-outstanding-${request.requestId}`}
      className="flex w-full flex-col gap-2"
    >
      <div className="flex min-w-0 flex-col gap-1">
        <ItemHeading
          title={request.title}
          summary={itemSummary(request.itemType, request.version)}
        />
        <DateList
          dates={[
            { label: "Sent", value: dates.sent },
            { label: "Due", value: dates.due },
            { label: "Read", value: dates.read },
          ]}
        />
      </div>
      <div className="flex shrink-0 items-center gap-2">
        <WorkspaceStatusBadge pill={acknowledgementRequestPill(request)} />
        <OutstandingAction
          request={request}
          isOwnProfile={isOwnProfile}
          showReminders={showReminders}
        />
      </div>
    </li>
  );
}

function SignedRow({ record }: { record: AcknowledgementRecordDto }) {
  const dates = completedDates(record);

  return (
    <li
      data-testid={`profile-ack-completed-${record.recordId}`}
      className="flex w-full flex-col gap-2"
    >
      <div className="flex min-w-0 flex-col gap-1">
        <ItemHeading
          title={record.itemTitle}
          summary={itemSummary(record.itemType, record.itemVersionNumber)}
        />
        <DateList
          dates={[
            { label: "Read", value: dates.read },
            { label: "Signed", value: dates.signed },
          ]}
        />
      </div>
      {record.supersededVersion && (
        <div
          data-testid={`profile-ack-superseded-${record.recordId}`}
          className="flex shrink-0 items-center"
        >
          <WorkspaceStatusBadge pill={ACKNOWLEDGEMENT_SUPERSEDED_PILL} />
        </div>
      )}
    </li>
  );
}

function ItemHeading({ title, summary }: { title: string; summary: string }) {
  return (
    <div className="flex min-w-0 flex-col">
      <span className="text-body-bold font-body-bold text-default-font break-words">
        {title}
      </span>
      <span className="text-caption font-caption text-subtext-color">
        {summary}
      </span>
    </div>
  );
}

function DateList({ dates }: { dates: { label: string; value: string }[] }) {
  return (
    <dl className="flex flex-wrap gap-x-6 gap-y-1">
      {dates.map((date) => (
        <div key={date.label} className="flex gap-1">
          <dt className="text-caption font-caption text-subtext-color">
            {date.label}
          </dt>
          <dd className="text-caption font-caption text-default-font">
            {date.value}
          </dd>
        </div>
      ))}
    </dl>
  );
}

function Muted({ children }: { children: string }) {
  return <span className="text-body text-subtext-color">{children}</span>;
}
